import React, { SyntheticEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './BadgeComponent.css';
import { Badge } from 'features/catalog/useBadges.ts';
import { AssociatedLink, iconForAssociatedLink } from 'features/catalog/badge-associated-links.ts';
import { unreleasedLockLine } from 'features/catalog/badge-release.ts';
import { talosBadgesLogoURL } from 'config/constants.ts';

/** Fallback when a badge image asset is missing. */
export const placeholderUrl = talosBadgesLogoURL;

/** In-app routes (e.g. `/stats`) — same-tab navigation; external URLs open in a new tab. */
const isInternalAssociatedLink = (link: AssociatedLink): boolean => link.href.startsWith('/');

/** A hint shown on hover for a locked badge (e.g. "No Self-claim", "Releases on: YYYY-MM-DD", or "Requires: X"). */
export interface BadgeUnlockHint {
  lines: string[];
  /** Whether this hint replaces the badge description rather than showing alongside it. */
  hidesDescription: boolean;
  /** Ids of the badges this one requires, if any (e.g. for the "Requires" hint). */
  requiredBadgeIds?: string[];
  /** Clickable names under the lock line; scroll to that badge / group on the claim page. */
  searchOnClickTargets?: { label: string; badgeId?: string; groupName?: string }[];
  /** When set, the lock `lines` themselves scroll to this badge / group on click. */
  unlockClickTarget?: { badgeId?: string; groupName?: string };
}

interface BadgeComponentProps {
  badge: Badge;
  onClick: (badge: Badge) => void;
  highlight?: boolean | null;
  display?: boolean;
  showName?: boolean;
  showDescription?: boolean;
  useDelay?: boolean;
  enableInteraction?: boolean;
  blackAndWhite?: boolean;
  smaller?: boolean;
  /** Compact image-only size for dense UIs (e.g. feed cards). */
  tiny?: boolean;
  showFloatingNameShadow?: boolean;
  lowOpacity?: boolean;
  showNameHeight?: boolean;
  hiddenName?: boolean;
  ideaIncluded?: boolean;
  designIncluded?: boolean;
  initiallyHidden?: boolean;
  isEditing?: boolean;
  cursorNotAllowed?: boolean;
  extraDescription?: BadgeUnlockHint | null;
  /** Shown on hover in a right-hand rail (like useful links); stacks below useful links when both exist */
  journeyCompletionHint?: { emoji?: string; lines: string[] } | null;
  /** In editing mode, badge was just claimed this session (not in original profile) */
  newlyClaimed?: boolean;
  /** In editing mode, badge was originally claimed but user removed it this session */
  removed?: boolean;
  /** In editing mode, badge is unclaimed - render smaller. Claimed badges stay 100% size. */
  compactInEditMode?: boolean;
  /** Hover useful-links rail — only create/edit profile; elsewhere links live on the badge modal. */
  showUsefulLinksOnHover?: boolean;
  /** Fires whenever this badge's hover state changes, so a parent can highlight related badges. */
  onHoverChange?: (hovered: boolean) => void;
  /** This badge's unlock requirement was just satisfied this session - plays a brief pop animation. */
  justUnlocked?: boolean;
  /** Soft nudge when this badge is the active “key” in an unlock-requirement wave (`loop` = solo key). */
  unlockKeyNudge?: false | 'once' | 'loop';
  /** Global claim rate label (e.g. "28%") — frosted overlay on image hover, under the description. */
  claimPctLabel?: string | null;
  /** Caption over the image. Stays visible on hover, centered in the area above the description. */
  imageOverlayLabel?: string | null;
  /** When a Requires name is clicked, scroll to that badge / group on the claim page. */
  onUnlockTargetClick?: (target: { badgeId?: string; groupName?: string }) => void;
  /** Replaces the hover description body. Unreleased badges ignore this and show “Releases on”. */
  hoverContent?: React.ReactNode;
  /** Keep `hoverContent` visible even when the badge is not hovered. */
  hoverContentAlways?: boolean;
  /** Tiny top-right marker on the image, e.g. "unclaimed" or "sample". */
  cornerPill?: string | null;
}

export const BadgeComponent = ({
  badge,
  onClick,
  highlight,
  display,
  enableInteraction = true,
  showName = enableInteraction,
  showDescription = enableInteraction,
  blackAndWhite = !enableInteraction,
  useDelay = false,
  smaller,
  tiny = false,
  showFloatingNameShadow = true,
  lowOpacity = false,
  showNameHeight = showName,
  hiddenName = false,
  designIncluded = false,
  ideaIncluded = false,
  initiallyHidden = false,
  isEditing = false,
  cursorNotAllowed = false,
  extraDescription = null,
  journeyCompletionHint = null,
  newlyClaimed = false,
  removed = false,
  compactInEditMode = false,
  showUsefulLinksOnHover = false,
  onHoverChange,
  justUnlocked = false,
  unlockKeyNudge = false,
  claimPctLabel = null,
  imageOverlayLabel = null,
  onUnlockTargetClick,
  hoverContent = null,
  hoverContentAlways = false,
  cornerPill = null,
}: BadgeComponentProps) => {
  const releaseLine = unreleasedLockLine(badge.id);
  const unlockHint = useMemo<BadgeUnlockHint | null>(
    () => extraDescription ?? (releaseLine ? { lines: [releaseLine], hidesDescription: true } : null),
    [extraDescription, releaseLine]
  );
  const [hovered, setHovered] = useState(false);
  const nameRef = useRef<HTMLParagraphElement>(null);
  const badgeInnerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [claimPctBottomPad, setClaimPctBottomPad] = useState(0);
  const delayString = useDelay ? `${Math.floor(Math.random() * 500) + 500}ms` : `0ms`;
  const effectiveShowName = tiny ? false : showName;
  const effectiveShowNameHeight = tiny ? false : showNameHeight;
  const effectiveShowDescription = tiny ? false : showDescription;
  const effectiveShowUsefulLinks = tiny || releaseLine != null ? false : showUsefulLinksOnHover;
  const hasUsefulLinks = Boolean(badge.associatedLinks && badge.associatedLinks.length > 0);
  const railUsefulLinks = hasUsefulLinks && effectiveShowUsefulLinks;
  const hasJourneyRail = !tiny && Boolean(journeyCompletionHint?.lines?.length);
  const showUsefulLinksPanel = railUsefulLinks && hovered;
  const showJourneyRailPanel = hasJourneyRail && hovered;
  const showSideRailHover = showUsefulLinksPanel || showJourneyRailPanel;
  const hasSideRailLayout = railUsefulLinks || hasJourneyRail;
  const showClaimPct = Boolean(claimPctLabel) && !tiny;
  const showImageOverlay = Boolean(imageOverlayLabel) && !tiny;
  const hasHoverContent = Boolean(hoverContent);
  const showDescriptionPanel = (hovered || hoverContentAlways) && (hasHoverContent || effectiveShowDescription);
  const hoverActive =
    enableInteraction &&
    (hasHoverContent || effectiveShowDescription || railUsefulLinks || hasJourneyRail || showClaimPct);

  const namePrefix = `${ideaIncluded ? '✏️ ' : ''}${designIncluded && !ideaIncluded ? '🎨 ' : ''}`;
  const visibleName = effectiveShowName ? badge.name : '';

  // Keep the punchy default size; shrink only when the label would wrap past 2 lines.
  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el || !visibleName) {
      if (el) el.style.fontSize = '';
      return;
    }

    el.style.fontSize = '';
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const basePx = parseFloat(getComputedStyle(el).fontSize) || 0.95 * rootPx;
    const overflows = () => el.scrollHeight > el.clientHeight + 1;
    if (!overflows()) return;

    // Binary search on the 0.025rem grid (~5 layouts instead of up to ~11 linear steps).
    const step = 0.025;
    const minSize = 0.68;
    const sizeAt = (steps: number) => basePx / rootPx - steps * step;
    // Base size (0 steps) already overflows; the floor (max steps) is used if nothing fits.
    let lo = 1;
    let hi = Math.max(1, Math.ceil((basePx / rootPx - minSize) / step));
    let best = hi;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      el.style.fontSize = `${sizeAt(mid)}rem`;
      if (overflows()) lo = mid + 1;
      else {
        best = mid;
        hi = mid - 1;
      }
    }
    el.style.fontSize = `${sizeAt(best)}rem`;
  }, [visibleName, namePrefix, hiddenName, smaller, tiny]);

  // Center overlay labels in the image above the description. Pad by how much
  // the description overlaps the image (desc is anchored to the column bottom,
  // which also includes the name).
  const shouldPadOverlay = showDescriptionPanel && (showImageOverlay || (hovered && showClaimPct));
  useLayoutEffect(() => {
    if (!shouldPadOverlay) {
      setClaimPctBottomPad(0);
      return;
    }
    const imageEl = badgeInnerRef.current;
    const descEl = descriptionRef.current;
    if (!imageEl || !descEl) {
      setClaimPctBottomPad(0);
      return;
    }
    const sync = () => {
      const imageRect = imageEl.getBoundingClientRect();
      const descRect = descEl.getBoundingClientRect();
      const overlap = Math.max(0, Math.min(imageRect.bottom, descRect.bottom) - Math.max(imageRect.top, descRect.top));
      setClaimPctBottomPad(Math.round(overlap));
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(descEl);
    observer.observe(imageEl);
    return () => observer.disconnect();
  }, [shouldPadOverlay, badge.description, unlockHint]);

  return (
    <div
      className={`badge rarity-${badge.rarityName} fadeIn ${tiny ? 'tiny border-radius' : ''} ${!tiny && smaller ? 'smaller border-radius' : ''} ${newlyClaimed ? 'newly-claimed' : ''} ${removed ? 'removed' : ''} ${compactInEditMode ? 'compact-in-edit' : ''} ${unlockHint ? 'has-unlock-hint' : ''} ${compactInEditMode && (cursorNotAllowed || !!unlockHint) ? 'claim-locked' : ''} ${highlight ? 'highlight' : ''} ${unlockKeyNudge ? `unlock-key-nudge${unlockKeyNudge === 'loop' ? ' unlock-key-nudge--loop' : ''}` : ''} ${showSideRailHover ? 'badge--useful-links-hover' : ''}`}
      style={{ '--delay': delayString }}
      onClick={() => enableInteraction && !cursorNotAllowed && onClick(badge)}
      title={tiny ? badge.name : undefined}
      data-badge-id={badge.id}
    >
      <div
        className={`badge-hover-layout${hasSideRailLayout ? ' badge-hover-layout--with-links' : ''}`}
        onMouseEnter={() => {
          if (hoverActive) {
            setHovered(true);
          }
          onHoverChange?.(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
          onHoverChange?.(false);
        }}
      >
        <div className={`badge-main-column ${justUnlocked ? 'just-unlocked' : ''}`}>
          <div
            ref={badgeInnerRef}
            className={`badge-inner ${initiallyHidden && !hiddenName && !isEditing ? 'hidden-name-removed' : ''} ${badge.rarityName}`}
          >
            {enableInteraction && !tiny && (
              <img
                className={`badge-rarity glow-effect-${badge.rarityName}`}
                src={badge.rarityImageUrl}
                alt={badge.rarityName}
                loading="lazy"
                decoding="async"
                onError={(e: SyntheticEvent<HTMLImageElement>) => {
                  if (e.currentTarget.src !== placeholderUrl) e.currentTarget.src = placeholderUrl;
                }}
              />
            )}
            <img
              className={`badge-image
          ${tiny ? 'tiny' : ''}
          ${!tiny && smaller ? 'smaller' : ''}
          ${!enableInteraction && 'disable-interaction'}
          ${blackAndWhite && 'black-and-white'}
          ${!enableInteraction ? (blackAndWhite ? 'opacity-lower-transition' : 'opacity-higher-transition') : ''}
          ${lowOpacity && 'low-opacity'}
          ${isEditing && badge.banSelfClaim && 'ban-self-claim'}
          ${cursorNotAllowed && 'cursor-not-allowed'}
          ${display && 'display'}`}
              src={badge.smallImageUrl}
              loading="lazy"
              decoding="async"
              onError={(e: SyntheticEvent<HTMLImageElement>) => {
                if (e.currentTarget.src !== placeholderUrl) e.currentTarget.src = placeholderUrl;
              }}
              alt={badge.name}
            />
            {cornerPill && !tiny ? (
              <span className="badge-unclaimed-pill" title={cornerPill}>
                <span className="badge-unclaimed-pill-mark" aria-hidden />
                <span className="badge-unclaimed-pill-label">{cornerPill}</span>
              </span>
            ) : null}
            {showClaimPct && hovered ? (
              <span
                className="badge-claim-pct-overlay"
                aria-hidden="true"
                style={claimPctBottomPad > 0 ? { paddingBottom: claimPctBottomPad } : undefined}
              >
                <span className="badge-claim-pct-stack">
                  <span className="badge-claim-pct-caption">claimed by</span>
                  <span className="badge-claim-pct-value">
                    {claimPctLabel?.endsWith('*') ? (
                      <>
                        {claimPctLabel.slice(0, -1)}
                        <span className="badge-claim-pct-star">*</span>
                      </>
                    ) : (
                      claimPctLabel
                    )}
                  </span>
                  {claimPctLabel?.endsWith('*') ? <span className="badge-claim-pct-footnote">since added</span> : null}
                </span>
              </span>
            ) : null}
            {showImageOverlay ? (
              <span
                className="badge-image-overlay"
                style={claimPctBottomPad > 0 ? { paddingBottom: claimPctBottomPad } : undefined}
              >
                <span className="badge-image-overlay-label">{imageOverlayLabel}</span>
              </span>
            ) : null}
          </div>
          {showDescriptionPanel && (
            <div
              ref={descriptionRef}
              className={`floating-description
          ${cursorNotAllowed && 'cursor-not-allowed'}
        `}
            >
              {unlockHint && (
                <div className="floating-description-unlock">
                  {unlockHint.lines.map((desc, i) =>
                    desc.split('\n').map((item, key) =>
                      unlockHint.unlockClickTarget && onUnlockTargetClick ? (
                        <button
                          key={`${i}-${key}`}
                          type="button"
                          className="floating-description-unlock-search"
                          onClick={(e: SyntheticEvent) => {
                            e.stopPropagation();
                            onUnlockTargetClick(unlockHint.unlockClickTarget!);
                          }}
                        >
                          {item}
                        </button>
                      ) : (
                        <i key={`${i}-${key}`}>{item}</i>
                      )
                    )
                  )}
                  {unlockHint.searchOnClickTargets?.map(({ label, badgeId, groupName }) =>
                    onUnlockTargetClick && (badgeId || groupName) ? (
                      <button
                        key={`${label}-${badgeId ?? groupName}`}
                        type="button"
                        className="floating-description-unlock-search"
                        onClick={(e: SyntheticEvent) => {
                          e.stopPropagation();
                          onUnlockTargetClick({ badgeId, groupName });
                        }}
                      >
                        {label}
                      </button>
                    ) : (
                      <i key={`${label}-${badgeId ?? groupName}`}>{label}</i>
                    )
                  )}
                </div>
              )}
              {unlockHint?.hidesDescription ? null : hoverContent ? hoverContent : <p>{badge.description}</p>}
            </div>
          )}
          {(effectiveShowName || effectiveShowNameHeight) && (
            <p
              ref={nameRef}
              className="floating-name"
              style={{
                background: showFloatingNameShadow
                  ? `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))`
                  : 'transparent',
              }}
            >
              {namePrefix}
              {visibleName}
              <span className="floating-name-locked">{hiddenName ? `🔒 ${badge.name}` : ''}</span>
            </p>
          )}
        </div>
        {showSideRailHover && (
          <>
            <div className="floating-useful-links-gap" aria-hidden="true" />
            <div className="badge-side-rails-column">
              {showUsefulLinksPanel && (
                <div className="floating-useful-links-rail" onClick={(e: SyntheticEvent) => e.stopPropagation()}>
                  <span className="floating-useful-links-label">Useful links</span>
                  {badge.associatedLinks!.map((link) => {
                    const icon = (
                      <img
                        src={iconForAssociatedLink(link)}
                        alt=""
                        className="floating-useful-link-icon"
                        onError={(e: SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.visibility = 'hidden';
                        }}
                      />
                    );
                    const label = <span className="floating-useful-link-text">{link.label}</span>;
                    if (isInternalAssociatedLink(link)) {
                      return (
                        <Link
                          key={link.href + link.label}
                          className="floating-useful-link"
                          to={link.href}
                          title={link.label}
                          onClick={(e: SyntheticEvent) => e.stopPropagation()}
                        >
                          {icon}
                          {label}
                        </Link>
                      );
                    }
                    return (
                      <a
                        key={link.href + link.label}
                        className="floating-useful-link"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`${link.label} — ${link.href}`}
                        onClick={(e: SyntheticEvent) => e.stopPropagation()}
                      >
                        {icon}
                        {label}
                      </a>
                    );
                  })}
                </div>
              )}
              {showJourneyRailPanel && journeyCompletionHint && (
                <div
                  className={`floating-journey-hint-rail${journeyCompletionHint.emoji ? ' floating-journey-hint-rail--has-emoji' : ''}`}
                  onClick={(e: SyntheticEvent) => e.stopPropagation()}
                >
                  <span className="floating-useful-links-label">Completes journey</span>
                  {journeyCompletionHint.lines.map((line, i) => (
                    <div key={i} className="floating-journey-hint-row">
                      <span className="floating-journey-hint-emoji-cell" aria-hidden>
                        {i === 0 && journeyCompletionHint.emoji ? journeyCompletionHint.emoji : ''}
                      </span>
                      <span className="floating-journey-hint-text">{line}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
